import * as React from "react";

import { dataProvider } from "@/providers/data-provider";

import { RuntimeDataContext } from "./runtime-data-context";

export function RuntimeDataProvider({ children }: { children: React.ReactNode }) {
  const [cache, setCache] = React.useState<Record<string, Record<string, unknown>[]>>({});

  const fetchResource = React.useCallback(async (resource: string) => {
    const result = await dataProvider.getList({ resource });
    setCache((prev) => ({ ...prev, [resource]: result.data as Record<string, unknown>[] }));
  }, []);

  const getData = React.useCallback(
    (resource: string) => cache[resource],
    [cache],
  );

  const refresh = React.useCallback(
    async (resource: string) => {
      await fetchResource(resource);
    },
    [fetchResource],
  );

  const create = React.useCallback(
    async (resource: string, variables: Record<string, unknown>) => {
      const result = await dataProvider.create({ resource, variables });
      await fetchResource(resource);
      return result.data as Record<string, unknown>;
    },
    [fetchResource],
  );

  const update = React.useCallback(
    async (resource: string, id: string, variables: Record<string, unknown>) => {
      const result = await dataProvider.update({ resource, id, variables });
      await fetchResource(resource);
      return result.data as Record<string, unknown>;
    },
    [fetchResource],
  );

  const deleteOne = React.useCallback(
    async (resource: string, id: string) => {
      const result = await dataProvider.deleteOne({ resource, id });
      await fetchResource(resource);
      return result.data as Record<string, unknown>;
    },
    [fetchResource],
  );

  return (
    <RuntimeDataContext.Provider value={{ cache, getData, refresh, create, update, deleteOne }}>
      {children}
    </RuntimeDataContext.Provider>
  );
}
